import { KGS_IN_LBS, kgToLbs, lbsToKg } from "../../../common/js/helpers"

export default () => ({
  init() {
    this.store = Alpine.store("data")
  },

  isMetric() {
    return this.store.values.measurementSystem === "metric"
  },

  getFutureDayDate(daysFromNow) {
    const now = new Date(Date.now())
    const futureDate = new Date(now.getTime() + daysFromNow * 24 * 60 * 60 * 1000)

    return futureDate.toLocaleDateString(pipelineConfig.language, {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  },

  normalizedHeight() {
    if (this.isMetric()) {
      return this.store.values.heightCm
    } else {
      return this.store.values.heightFt * 30.48 + this.store.values.heightIn * 2.54
    }
  },

  idealWeight() {
    return Math.round((this.normalizedHeight() * 2 * 21.1) / 10000)
  },

  normalizedWeight() {
    if (this.isMetric()) {
      return this.store.values.weightKg
    } else {
      return this.store.values.weightLbs
    }
  },

  normalizedTargetWeight() {
    if (this.isMetric()) {
      return this.store.values.targetWeightKg
    } else {
      return this.store.values.targetWeightLbs * KGS_IN_LBS
    }
  },

  targetOverWeight() {
    return Math.ceil(this.normalizedWeight() - this.normalizedTargetWeight())
  },

  overWeight() {
    return this.normalizedWeight() - this.idealWeight()
  },

  firstMonth() {
    let val = (30 / 100) * this.overWeight()

    if (val > 7) {
      return 7
    } else if (val < 4) {
      return 4
    }

    return Math.floor(val)
  },

  getNextWeeksWeightLossResult(weeks = 1) {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values

    const currentWeight = this.store.isMetric() ? weightKg : weightLbs
    const targetWeight = this.store.isMetric() ? targetWeightKg : targetWeightLbs

    // Product requirement for the edge cases:
    // "For all cases when (Current weight - target weight) < 7kg,
    // Return current weight minus 5kg for the first week and minus 7kg for the next weeks"

    const targetDiff = currentWeight - targetWeight

    const edgeCaseDiffGeneral = this.store.isMetric() ? 7 : kgToLbs(7)
    const edgeCaseDiffFirstWeek = this.store.isMetric() ? 5 : kgToLbs(5)

    if (targetDiff < edgeCaseDiffGeneral) {
      if (weeks === 1) {
        return Math.round(currentWeight - edgeCaseDiffFirstWeek)
      } else {
        return Math.round(currentWeight - edgeCaseDiffGeneral)
      }
    }

    // Product requirement for normal cases:
    // The first week is always:
    // "Current minus 5kg or 1/3 of the (current weight - target weight) (whichever is smaller)"
    // "95 - 5kg (because 95/3 = 31.6 > 5kg)"
    // All the next weeks after the first one:
    // "(2kg x N(weeks)"
    // So result is: current - first week - all next

    const maxFirstWeekLoss = this.store.isMetric() ? 5 : kgToLbs(5)
    const firstWeekLoss = Math.min(targetDiff / 3, maxFirstWeekLoss)

    if (weeks === 1) {
      return Math.round(currentWeight - firstWeekLoss)
    } else {
      const perWeekLoss = this.store.isMetric() ? 2 : kgToLbs(2)
      // Not include already calculated first week
      const nextWeeksLoss = perWeekLoss * (weeks - 1)

      return Math.round(currentWeight - firstWeekLoss - nextWeeksLoss)
    }
  },

  getCurrentWeight() {
    const { weightKg, weightLbs } = this.store.values
    return this.store.isMetric() ? weightKg : weightLbs
  },

  getTargetWeight() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values

    const currentWeight = this.store.isMetric() ? weightKg : weightLbs
    const targetWeight = this.store.isMetric() ? targetWeightKg : targetWeightLbs

    const targetDiff = currentWeight - targetWeight

    const edgeCaseDiff = this.store.isMetric() ? 7 : kgToLbs(7)

    // If current minus target < 7kg, we show current minus 7kg, ignoring user input
    if (targetDiff < edgeCaseDiff) {
      return Math.round(currentWeight - edgeCaseDiff)
    } else {
      return Math.round(targetWeight)
    }
  },

  getFirstWeekDate() {
    return this.getFutureDayDate(7)
  },

  // The date by which the target weight will be achieved
  // based on the same formula as for the next weeks loss estimation
  getGoalDate() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values
    const currentWeight = this.store.isMetric() ? weightKg : weightLbs
    const targetWeight = this.store.isMetric() ? targetWeightKg : targetWeightLbs

    // Calculate weight diffs between current and target
    const targetDiff = currentWeight - targetWeight
    const edgeCaseDiff = this.store.isMetric() ? 7 : kgToLbs(7)

    // Calculate first week loss, as pace is different as per requirements
    const maxFirstWeekLoss = this.store.isMetric() ? 5 : kgToLbs(5)

    const firstWeekLoss = Math.min(targetDiff / 3, maxFirstWeekLoss)

    // Calculate how many more weeks after first needed to achieve the goal
    const perWeekLoss = this.store.isMetric() ? 2 : kgToLbs(2)
    const moreWeeksNeeded = Math.round((targetDiff - firstWeekLoss) / perWeekLoss)

    // For the edge cases scenarios we just set fixed goal weight
    // and show goal date in a 2 weeks
    if (targetDiff < edgeCaseDiff || targetDiff <= firstWeekLoss || moreWeeksNeeded <= 2) {
      return this.getFutureDayDate(14)
    } else {
      return this.getFutureDayDate(7 * (moreWeeksNeeded + 1))
    }
  },
})
